import {
  EquipmentCondition,
  EquipmentType,
  SiteEquipmentPageEquipmentStatus,
  SiteEquipmentPageParams,
} from "./types";

type QueryValue = string | number | boolean | string[];
type QueryRecord = Record<string, QueryValue>;

function toArrayOrSingle<T>(value: T | T[] | undefined): T[] | undefined {
  if (!value) return undefined;
  return Array.isArray(value) ? value : [value];
}

function assignIfDefined(
  query: QueryRecord,
  key: string,
  value: string | number | boolean | undefined
) {
  if (value === undefined) return;
  if (typeof value === "string" && value.length === 0) return;
  query[key] = value;
}

function assignArrayParam<T extends string>(
  query: QueryRecord,
  key: string,
  value: T | T[] | undefined
) {
  const values = toArrayOrSingle(value);
  if (!values?.length) return;
  query[key] = values;
}

function assignPaginationParams(
  query: QueryRecord,
  params: SiteEquipmentPageParams
) {
  if (params.allowAll) {
    query.allow_all = true;
    return;
  }

  assignIfDefined(query, "page", params.page);
  assignIfDefined(query, "page_size", params.pageSize);
}

export function siteEquipmentPageParamsToQuery(
  params: SiteEquipmentPageParams
): QueryRecord {
  const query: QueryRecord = {};

  assignPaginationParams(query, params);

  (
    [
      ["include_preview", params.includePreview],
      ["include_summary", params.includeSummary],
      ["building", params.building],
      ["backpack_verified", params.backpackVerified],
      ["include_decommissioned", params.includeDecommissioned],
      ["on_watch_list", params.onWatchList],
      ["linked_to_project", params.linkedToProject],
      ["search", params.search],
      ["sort", params.sort],
    ] as const
  ).forEach(([key, value]) => {
    assignIfDefined(query, key, value);
  });

  assignArrayParam(query, "equipment_type", params.equipmentType);
  assignArrayParam(query, "equipment_status", params.equipmentStatus);
  assignArrayParam(query, "equipment_condition", params.equipmentCondition);

  return query;
}

export type {
  EquipmentType,
  EquipmentCondition,
  SiteEquipmentPageEquipmentStatus,
};
